import { useEffect, useMemo, useState } from "react";
import { CircleNotch, Coins } from "@phosphor-icons/react";
import { repApi } from "@/lib/repApi";

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

const STATUS_LABEL = {
  pending: "Em carência",
  liberable: "Liberada",
  paid: "Paga",
  cancelled: "Cancelada",
};

const STATUS_STYLE = {
  pending:   "bg-amber-500/10 text-amber-300",
  liberable: "bg-emerald-500/10 text-emerald-300",
  paid:      "bg-sky-500/10 text-sky-300",
  cancelled: "bg-slate-500/10 text-slate-400",
};

const FILTERS = [
  { value: "",          label: "Todas" },
  { value: "pending",   label: "Em carência" },
  { value: "liberable", label: "Liberadas" },
  { value: "paid",      label: "Pagas" },
  { value: "cancelled", label: "Canceladas" },
];

export default function RepCommissions() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setItems(null);
    const url = filter ? `/rep/commissions?status_filter=${filter}` : "/rep/commissions";
    repApi.get(url).then(({ data }) => setItems(data));
  }, [filter]);

  const totals = useMemo(() => {
    if (!items) return { count: 0, sum: 0 };
    return items.reduce(
      (acc, c) => ({ count: acc.count + 1, sum: acc.sum + (c.commission_cents || 0) }),
      { count: 0, sum: 0 },
    );
  }, [items]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Portal do Representante
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
          Comissões
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          {items === null
            ? "Carregando…"
            : (
              <>
                {totals.count} {totals.count === 1 ? "comissão" : "comissões"} ·{" "}
                <span className="text-white tabular-nums">{fmtBRL(totals.sum)}</span>
                {filter && ` em ${FILTERS.find((f) => f.value === filter)?.label.toLowerCase()}`}
              </>
            )}
        </p>
      </header>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setFilter(f.value)}
            data-testid={`rep-commissions-filter-${f.value || "all"}`}
            className={`text-[12px] px-3 py-1.5 rounded-md transition-colors ${
              filter === f.value
                ? "bg-white/10 text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-left">
            <tr>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Cliente</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Base</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">%</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Comissão</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Pago em</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Libera em</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {items === null && (
              <tr><td colSpan="7" className="p-12 text-center">
                <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
              </td></tr>
            )}
            {items?.length === 0 && (
              <tr><td colSpan="7" className="p-12 text-center text-slate-500 text-sm">
                <Coins size={20} weight="regular" className="inline-block mb-2 text-slate-600" />
                <div>Nenhuma comissão neste filtro.</div>
              </td></tr>
            )}
            {items?.map((c) => (
              <tr
                key={c.id}
                className="border-t border-white/5"
                data-testid={`rep-commission-row-${c.id}`}
              >
                <td className="px-4 py-3">
                  <div className="text-white font-medium truncate max-w-[260px]">
                    {c.company_name || "—"}
                  </div>
                  {c.stripe_invoice_id && (
                    <div className="text-[10px] text-slate-500 font-mono">
                      {c.stripe_invoice_id}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
                  {fmtBRL(c.amount_paid_cents)}
                </td>
                <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
                  {((c.commission_pct || 0) * 100).toFixed(0)}%
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-400 font-semibold">
                  {fmtBRL(c.commission_cents)}
                </td>
                <td className="px-4 py-3 text-slate-400 text-[12px]">{fmtDate(c.paid_at)}</td>
                <td className="px-4 py-3 text-slate-400 text-[12px]">{fmtDate(c.liberable_at)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${STATUS_STYLE[c.status] || ""}`}
                  >
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
